import { Avatar, IconButton, MenuItem } from "@mui/material";
import { useState } from "react";
import { useRecoilValue } from "recoil";
import { useTranslation } from "react-i18next";
import { GoMakeMenu } from "@/components";
import { PencilLineIcon } from "@/icons";
import { useGomakeRouter } from "@/hooks";
import { userState } from "@/store/user";
import { userProfileState } from "@/store/user-profile";
import { useTheme } from "@/contexts/theme";
import { startHostedLogout } from "@/utils/hosted-login";
import { CustomTooltip } from "../custom-tooltip";
import { useStyle } from "../../style";

interface IUserProfileMenuProps {
  /** Rendered avatar size in px. Defaults to the desktop header size. */
  avatarSize?: number;
}

/**
 * Avatar entry point to the account menu, shared by the desktop and mobile headers.
 */
const UserProfileMenu = ({ avatarSize }: IUserProfileMenuProps) => {
  const { classes, primaryColor, errorColor, neutralColor } = useStyle();
  const { t } = useTranslation();
  const { navigate } = useGomakeRouter();
  const user = useRecoilValue<any>(userState);
  const userProfile = useRecoilValue(userProfileState);
  const { getIconOverride } = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const UserIcon = getIconOverride("UserIcon");
  const avatarStyle = {
    ...classes.avatarProps,
    ...(avatarSize ? { width: avatarSize, height: avatarSize } : {}),
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const userAvatar = () => {
    if (UserIcon) {
      const size = avatarSize ?? 32;
      return <UserIcon width={size} height={size} />;
    }

    return !!userProfile.imagePath ? (
      <Avatar style={avatarStyle} src={userProfile.imagePath} />
    ) : (
      <Avatar
        style={{
          backgroundColor: userProfile.avatarBackGroundColor,
          ...avatarStyle,
        }}
      >
        {userProfile.avatarInitials?.toUpperCase()}
      </Avatar>
    );
  };

  return (
    <>
      <div style={classes.profileContainer}>
        <CustomTooltip
          title={`${user?.firstName} ${user?.lastName}`}
          arrow
          placement={"bottom-start"}
        >
          <IconButton
            style={{ padding: "2px" }}
            onClick={handleClick}
            aria-label={t("login.account")}
          >
            {userAvatar()}
          </IconButton>
        </CustomTooltip>
      </div>
      <GoMakeMenu
        handleClose={handleClose}
        open={open}
        anchorEl={anchorEl}
        menuOffsetY={1}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
            border: `1px solid ${neutralColor(200)}`,
            overflow: "hidden",
            padding: 0,
          },
          "& .MuiList-root": {
            padding: 0,
          },
        }}
      >
        <div style={classes.mainMenuContainer}>
          <div style={classes.accountTextStyle}>{t("login.account")}</div>
          <div style={classes.imgNameContainer}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {userAvatar()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={classes.nameTextStyle}
              >{`${user?.firstName} ${user?.lastName}`}</div>
              <div style={classes.emailTextStyle}>{user?.email}</div>
            </div>
          </div>
          <MenuItem
            sx={{
              width: "100%",
              padding: "6px 12px",
              margin: "6px 0 2px 0",
              borderRadius: "6px",
              minHeight: "auto",
              "&:hover": {
                backgroundColor: `${primaryColor(50)} !important`,
              },
            }}
            onClick={() => {
              navigate("/settings/profile");
              handleClose();
            }}
            onMouseDown={(e) => {
              if (e.button === 1) {
                window.open("/settings/profile", "_blank");
              }
            }}
          >
            <div style={classes.manageAccountStyle}>
              <div style={classes.manageAccountTextStyle}>
                {t("login.manageAccount")}
              </div>
              <PencilLineIcon
                style={{ fontSize: 16 }}
                stroke={primaryColor(500)}
              />
            </div>
          </MenuItem>
          <div style={classes.lineContainer} />
          <MenuItem
            sx={{
              width: "100%",
              padding: "6px 12px",
              margin: "2px 0",
              borderRadius: "6px",
              minHeight: "auto",
              "&:hover": {
                backgroundColor: `${errorColor(100)} !important`,
              },
            }}
            onClick={() => {
              // Also ends the Cognito Hosted UI session; otherwise the user is silently
              // re-authenticated when /login bounces back to Cognito.
              startHostedLogout();
            }}
          >
            <div style={classes.logoutContainer}>{t("login.logOut")}</div>
          </MenuItem>
        </div>
      </GoMakeMenu>
    </>
  );
};

export { UserProfileMenu };
